/**
 * PipelinePropertyFilter — the SFDC pipeline-property filter for the Pipeline View
 * (design 2026-07-29 §5.1/§5.2). A Cloudscape PropertyFilter with one-level AND/OR
 * grouping (enableTokenGroups) over five opp fields: SFDC Stage / Account Name /
 * Oppty Name / Opp ID / Opp Owner. Enum-only operators (= / !=) → each token is a
 * multi-select (IN / NOT IN); (A OR B) AND (C OR D) combos are expressible.
 *
 * Candidate values come from the parent (usePipelineFilterOptions → GetPipelineFilterOptions):
 * Stage is a fixed 7-bucket set; the identity fields search server-side via onLoadItems.
 * This component is purely presentational — the parent owns query state, translation
 * to the backend param (queryToPropertyFilter), and the options fetch.
 */
import { PropertyFilter, type PropertyFilterProps } from '@amzn/awsui-components-react';
import FloatingLabelField from '../../../components/FloatingLabelField/FloatingLabelField';
import {
  DATE_OPERATORS,
  ENUM_OPERATORS,
  GREENFIELD_OPERATORS,
  HIERARCHY_OPERATORS,
  TAG_MATCHES_HIERARCHY_OPTIONS,
  BOOLEAN_VALUES,
  IS_GREENFIELD_OPTIONS as GREENFIELD_LABEL_OPTIONS,
  PROPERTY_FILTER_I18N,
} from '../../../components/pipelineFilterShared';

/** The SFDC property fields. `key` MUST equal the backend field enum values
 *  (SfdcPipelineFilterField) so queryToPropertyFilter maps propertyKey → field 1:1.
 *  Three field categories:
 *   - enum-searchable (Stage / Account / Oppty / OppId / Owner / Score): = / != only,
 *     candidate values from GetPipelineFilterOptions (parent-fed).
 *   - date (Created / Close): = / != / < / <= / > / >= with a DatePicker popover.
 *   - boolean flag (Greenfield / BigBet / IVT参与): = / != , hardcoded true/false.
 *  Date + boolean fields reached parity with the Impact page in CR-299674749 (backend)
 *  + this frontend CR; all three categories share operators/forms via pipelineFilterShared. */
const FILTERING_PROPERTIES: readonly PropertyFilterProps.FilteringProperty[] = [
  { key: 'SFDC_STAGE', propertyLabel: 'SFDC Stage', groupValuesLabel: 'SFDC Stage values', operators: ENUM_OPERATORS, defaultOperator: '=' },
  { key: 'ACCOUNT_NAME', propertyLabel: 'Account Name', groupValuesLabel: 'Account Name values', operators: ENUM_OPERATORS, defaultOperator: '=' },
  { key: 'OPP_NAME', propertyLabel: 'Opportunity Name', groupValuesLabel: 'Opportunity Name values', operators: ENUM_OPERATORS, defaultOperator: '=' },
  { key: 'OPP_ID', propertyLabel: 'Opportunity ID', groupValuesLabel: 'Opportunity ID values', operators: ENUM_OPERATORS, defaultOperator: '=' },
  { key: 'OPP_OWNER', propertyLabel: 'Opportunity Owner', groupValuesLabel: 'Opportunity Owner values', operators: ENUM_OPERATORS, defaultOperator: '=' },
  // Engagement Depth Score (DAT-8462 Phase 2): fixed whole-set enum field — candidate
  // values (the "N分" labels) come from GetPipelineFilterOptions(PIPELINE_SCORE).
  { key: 'PIPELINE_SCORE', propertyLabel: 'Engagement Depth Score', groupValuesLabel: 'Engagement Depth Score values', operators: ENUM_OPERATORS, defaultOperator: '=' },
  // Date-range fields (parity w/ Impact, CR-299674749): scalar comparison + = / !=,
  // DatePicker popover. Value is ISO YYYY-MM-DD.
  { key: 'CREATED_DATE', propertyLabel: 'Create Date', groupValuesLabel: 'Create Date values', operators: DATE_OPERATORS, defaultOperator: '>=' },
  { key: 'CLOSE_DATE', propertyLabel: 'Close Date', groupValuesLabel: 'Close Date values', operators: DATE_OPERATORS, defaultOperator: '>=' },
  // Boolean flags (parity w/ Impact, CR-299674749): = / != , hardcoded true/false,
  // single-value enforced in sfdcPipelinePropertyFilter.ts BOOL_SINGLE_VALUE_FIELDS.
  { key: 'IS_BIG_BET', propertyLabel: 'Big Bet Pipelines', groupValuesLabel: 'Big Bet values', operators: ENUM_OPERATORS, defaultOperator: '=' },
  { key: 'HAS_IVT_ACTIVITY', propertyLabel: 'IVT直接参与', groupValuesLabel: 'IVT参与 values', operators: ENUM_OPERATORS, defaultOperator: '=' },
  // 处理方式 (IS_GREENFIELD, BOOL): Greenfield = SFDC sales-level L3 = "IND GFD", else
  // Industry. GREENFIELD_OPERATORS adds a `format` fn so the applied token reads
  // Greenfield/Industry (not raw true/false); hardcoded options injected below.
  { key: 'IS_GREENFIELD', propertyLabel: 'Greenfield or Industry', groupValuesLabel: 'Greenfield or Industry values', operators: GREENFIELD_OPERATORS, defaultOperator: '=' },
  // Options name a SCOPE (Exclude Cross / Only Cross) so neither token can be misread as
  // "no filtering" — that state is "no token", not a value. See formatTagMatchesHierarchy.
  { key: 'TAG_MATCHES_HIERARCHY', propertyLabel: 'Exclude Cross-Industry Tagging', groupValuesLabel: 'Exclude Cross-Industry Tagging values', operators: HIERARCHY_OPERATORS, defaultOperator: '=' },
];

/** Hardcoded boolean candidate values (not from GetPipelineFilterOptions — a BOOL
 *  isn't a searchable pool). IS_GREENFIELD carries Greenfield/Industry labels;
 *  IS_BIG_BET / HAS_IVT_ACTIVITY show raw true/false (parity with the Impact page). */
const BOOLEAN_OPTIONS: readonly PropertyFilterProps.FilteringOption[] = [
  ...GREENFIELD_LABEL_OPTIONS.map((o) => ({ propertyKey: 'IS_GREENFIELD', value: o.value, label: o.label })),
  ...BOOLEAN_VALUES.map((v) => ({ propertyKey: 'IS_BIG_BET', value: v })),
  ...BOOLEAN_VALUES.map((v) => ({ propertyKey: 'HAS_IVT_ACTIVITY', value: v })),
  ...TAG_MATCHES_HIERARCHY_OPTIONS.map((o) => ({ propertyKey: 'TAG_MATCHES_HIERARCHY', value: o.value, label: o.label })),
];

// Shared with every other IVT PropertyFilter — see PROPERTY_FILTER_I18N. Only
// filteringAriaLabel differs per control, so it is spread in here.
const I18N: PropertyFilterProps.I18nStrings = {
  ...PROPERTY_FILTER_I18N,
  filteringAriaLabel: 'Filter pipeline by SFDC properties',
};

export interface PipelinePropertyFilterProps {
  query: PropertyFilterProps.Query;
  onChange: (query: PropertyFilterProps.Query) => void;
  filteringOptions: readonly PropertyFilterProps.FilteringOption[];
  filteringStatusType: PropertyFilterProps.StatusType;
  onLoadItems: PropertyFilterProps['onLoadItems'];
}

export default function PipelinePropertyFilter({
  query,
  onChange,
  filteringOptions,
  filteringStatusType,
  onLoadItems,
}: PipelinePropertyFilterProps) {
  // Server-fetched candidate values (Stage/Score/identity fields) + the hardcoded
  // boolean options (Greenfield/BigBet/IVT参与). Booleans are static so they're not
  // part of the GetPipelineFilterOptions fetch cycle. Date fields have no candidate
  // list (DatePicker popover), so they contribute nothing here.
  const allOptions = [...filteringOptions, ...BOOLEAN_OPTIONS];
  return (
    // Full-width row (design §5.1): the SFDC property filter under the dropdown row.
    // Uses the shared <FloatingLabelField> for the notched "Pipeline Property" title
    // so it stays pixel-consistent with the MotionFilters cells; layout (full-width
    // block, bottom gap) is owned here via .ivt-pipeline-property-filter.
    <FloatingLabelField label="Pipeline Property" className="ivt-pipeline-property-filter">
      <PropertyFilter
        query={query}
        onChange={({ detail }) => onChange(detail)}
        filteringProperties={FILTERING_PROPERTIES}
        filteringOptions={allOptions}
        filteringStatusType={filteringStatusType}
        onLoadItems={onLoadItems}
        enableTokenGroups
        expandToViewport
        // No free-text filtering: every field is enum multi-select (pick from the
        // candidate list), so the "All properties" free-text entry is meaningless
        // here — disabling it drops that option from the property dropdown.
        disableFreeTextFiltering
        hideOperations={false}
        filteringPlaceholder="Filter by Stage / Account / Opportunity / Owner / Score / ..."
        filteringAriaLabel="Filter pipeline by SFDC properties"
        i18nStrings={I18N}
        filteringLoadingText="Loading options"
        filteringErrorText="Error fetching options"
        filteringRecoveryText="Retry"
        filteringFinishedText="End of results"
      />
    </FloatingLabelField>
  );
}
